/**
 * Opening cinematic (~22 s, skippable): The Scientist's lab → the evolution ray's dial
 * slips to REVERSE → portal-green zap → everything shrinks → microscope view of a single
 * cell (you). Stylised SVG art animated from a JS timeline, subtitles from INTRO_LINES.
 */
import type { AudioApi } from '@core/services';
import { el, escapeHtml } from '../dom';
import { ICONS } from '../icons';
import { INTRO_LINES } from '../lines';

/** Real length of the cinematic (s). The art timeline below is authored in 22.4 s "story time". */
const DURATION = 10;
const STORY_END = 22.4;
/** Real seconds → story seconds (piecewise: lab, dial, zap/shrink, microscope). */
const WARP: Array<[number, number]> = [
  [0, 0],
  [3.5, 8.6],
  [5.2, 11.2],
  [7.4, 15.2],
  [10, 22.4],
];
function warp(rt: number): number {
  for (let i = 1; i < WARP.length; i++) {
    const [r0, s0] = WARP[i - 1];
    const [r1, s1] = WARP[i];
    if (rt <= r1) return s0 + ((rt - r0) / (r1 - r0)) * (s1 - s0);
  }
  return STORY_END;
}

function rng(seed: number): () => number {
  let s = seed;
  return () => ((s = (s * 16807) % 2147483647) - 1) / 2147483646;
}

function labSvg(): string {
  const r = rng(7);
  // Shelves of glowing flasks.
  let flasks = '';
  const cols = ['#97ff5a', '#5bf0ff', '#c06bff', '#ffd86b', '#3dffb0'];
  for (let shelf = 0; shelf < 3; shelf++) {
    const y = 200 + shelf * 120;
    flasks += `<rect x="930" y="${y}" width="560" height="10" rx="3" fill="#1d2e28"/>`;
    for (let i = 0; i < 7; i++) {
      if (r() < 0.2) continue;
      const x = 950 + i * 78 + r() * 20;
      const h = 40 + r() * 40;
      const c = cols[Math.floor(r() * cols.length)];
      const round = r() < 0.5;
      flasks += round
        ? `<g class="flask" style="--d:${(r() * 3).toFixed(2)}s"><rect x="${x + 12}" y="${y - h}" width="10" height="${h * 0.5}" fill="#2a3d37" opacity=".8"/><circle cx="${x + 17}" cy="${y - h * 0.3}" r="${h * 0.3}" fill="#12211c" stroke="#3a5a50" stroke-width="2"/><circle cx="${x + 17}" cy="${y - h * 0.22}" r="${h * 0.22}" fill="${c}" opacity=".85" class="liq"/></g>`
        : `<g class="flask" style="--d:${(r() * 3).toFixed(2)}s"><rect x="${x}" y="${y - h}" width="30" height="${h}" rx="6" fill="#12211c" stroke="#3a5a50" stroke-width="2"/><rect x="${x + 3}" y="${y - h * 0.62}" width="24" height="${h * 0.6}" rx="4" fill="${c}" opacity=".85" class="liq"/></g>`;
    }
  }
  // Bubbles in the specimen tank.
  let bubbles = '';
  for (let i = 0; i < 14; i++) {
    bubbles += `<circle class="bub" cx="${250 + r() * 120}" cy="720" r="${3 + r() * 6}" style="--d:${(r() * 4).toFixed(2)}s;--x:${((r() - 0.5) * 30).toFixed(0)}px" />`;
  }
  // Microscope scene cells.
  let micro = '';
  for (let i = 0; i < 16; i++) {
    const a = r() * Math.PI * 2;
    const d = 180 + r() * 220;
    const rr = 14 + r() * 26;
    const c = cols[Math.floor(r() * cols.length)];
    micro += `<g class="mcell" style="--d:${(r() * 6).toFixed(2)}s;--dx:${((r() - 0.5) * 60).toFixed(0)}px;--dy:${((r() - 0.5) * 60).toFixed(0)}px"><circle cx="${800 + Math.cos(a) * d}" cy="${450 + Math.sin(a) * d * 0.8}" r="${rr}" fill="${c}" fill-opacity=".14" stroke="${c}" stroke-opacity=".7" stroke-width="2"/><circle cx="${800 + Math.cos(a) * d + rr * 0.2}" cy="${450 + Math.sin(a) * d * 0.8 - rr * 0.1}" r="${rr * 0.32}" fill="${c}" fill-opacity=".5"/></g>`;
  }
  return `<svg class="lab-art" viewBox="0 0 1600 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
  <defs>
    <radialGradient id="ilamp" cx="50%" cy="0%" r="80%"><stop offset="0" stop-color="#2c6b4a" stop-opacity=".9"/><stop offset="1" stop-color="#04100b" stop-opacity="0"/></radialGradient>
    <linearGradient id="iwall" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0b1e17"/><stop offset="1" stop-color="#06120d"/></linearGradient>
    <linearGradient id="ifloor" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f261d"/><stop offset="1" stop-color="#030906"/></linearGradient>
    <linearGradient id="icoat" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f2f7f4"/><stop offset="1" stop-color="#a9bdb5"/></linearGradient>
    <linearGradient id="ihair" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#f4f7ff"/><stop offset="1" stop-color="#8fa2bd"/></linearGradient>
    <radialGradient id="itank" cx="50%" cy="50%" r="60%"><stop offset="0" stop-color="#6bff9a" stop-opacity=".55"/><stop offset="1" stop-color="#0d4d2a" stop-opacity=".35"/></radialGradient>
    <radialGradient id="ibeam" cx="50%" cy="50%" r="50%"><stop offset="0" stop-color="#fff"/><stop offset=".3" stop-color="#c9ff8a"/><stop offset="1" stop-color="#97ff5a" stop-opacity="0"/></radialGradient>
    <radialGradient id="iscope" cx="50%" cy="50%" r="50%"><stop offset="0" stop-color="#123a26"/><stop offset=".85" stop-color="#07170f"/><stop offset="1" stop-color="#000"/></radialGradient>
    <radialGradient id="ihero" cx="40%" cy="35%" r="65%"><stop offset="0" stop-color="#eaffd8"/><stop offset=".45" stop-color="#97ff5a" stop-opacity=".7"/><stop offset="1" stop-color="#2f8a2a" stop-opacity=".25"/></radialGradient>
  </defs>
  <g class="shot lab">
    <g class="cam">
      <rect width="1600" height="900" fill="url(#iwall)"/>
      <g stroke="#15302a" stroke-width="2" opacity=".8">${Array.from({ length: 9 }, (_, i) => `<line x1="${i * 200}" y1="0" x2="${i * 200}" y2="640"/>`).join('')}<line x1="0" y1="140" x2="1600" y2="140"/></g>
      <rect y="640" width="1600" height="260" fill="url(#ifloor)"/>
      <ellipse cx="800" cy="0" rx="700" ry="420" fill="url(#ilamp)"/>
      <!-- lamp -->
      <path d="M760 0v60M720 60h80l-10 26h-60z" stroke="#2c4a40" stroke-width="6" fill="#1a2f28"/>
      <ellipse cx="760" cy="90" rx="40" ry="8" fill="#e9ffd9" class="lampglow"/>
      ${flasks}
      <!-- specimen tank -->
      <g class="tank">
        <rect x="220" y="240" width="180" height="420" rx="90" fill="url(#itank)" stroke="#6fbf93" stroke-width="5"/>
        <path d="M268 470c20-40 70-44 90-20 10 12 8 30-8 38-14 6-30 0-34 16-6 26-44 18-50-6-3-10-1-20 2-28z" fill="#0b2a1a" opacity=".85"/>
        <circle cx="330" cy="460" r="5" fill="#c9ff8a"/>
        ${bubbles}
        <rect x="200" y="640" width="220" height="40" rx="8" fill="#1d2e28"/>
        <rect x="210" y="220" width="200" height="30" rx="8" fill="#1d2e28"/>
      </g>
      <!-- workbench -->
      <rect x="560" y="610" width="520" height="24" rx="6" fill="#23372f"/>
      <rect x="590" y="634" width="20" height="160" fill="#1a2a24"/><rect x="1030" y="634" width="20" height="160" fill="#1a2a24"/>
      <!-- evolution ray on tripod -->
      <g class="ray">
        <path d="M1180 820l70-230 70 230M1250 590v230" stroke="#2c4a40" stroke-width="10" fill="none"/>
        <g class="barrel">
          <rect x="1090" y="520" width="300" height="80" rx="30" fill="#3b4f48" stroke="#6b8a7e" stroke-width="4"/>
          <rect x="1300" y="505" width="70" height="110" rx="16" fill="#2b3b36" stroke="#6b8a7e" stroke-width="4"/>
          <g class="coils">${[0, 1, 2, 3].map((i) => `<ellipse cx="${1060 - i * 26}" cy="560" rx="${14 + i * 2}" ry="${44 + i * 6}" fill="none" stroke="#97ff5a" stroke-width="5" opacity="${0.9 - i * 0.15}"/>`).join('')}</g>
          <circle cx="990" cy="560" r="20" fill="#c9ff8a" class="muzzle"/>
          <circle cx="1335" cy="560" r="22" fill="#10201a" stroke="#97ff5a" stroke-width="3"/>
          <path class="needle-s" d="M1335 560l0-16" stroke="#ff4f6d" stroke-width="4" stroke-linecap="round"/>
        </g>
      </g>
      <!-- The Scientist -->
      <g class="sci">
        <path d="M600 900c10-150 40-260 110-300 40-20 100-20 140 0 70 40 100 150 110 300z" fill="url(#icoat)"/>
        <path d="M740 600l40 70 40-70" fill="#2b3b36"/>
        <path d="M700 620c-40 30-60 80-50 140l40-4c-2-50 10-90 40-110z" fill="#dbe6e1"/>
        <g class="arm"><path d="M860 620c50 10 110-10 160-50l20 26c-50 50-120 76-180 64z" fill="#dbe6e1"/><circle cx="1036" cy="584" r="16" fill="#e7c8b4"/></g>
        <rect x="762" y="560" width="36" height="46" fill="#d8b49f"/>
        <ellipse cx="780" cy="500" rx="64" ry="74" fill="#efd6c4"/>
        <path d="M712 470c-26-40-10-90 30-106-4 18 4 26 16 26-8-30 14-56 46-58-6 14-2 24 12 26 6-22 36-30 58-18-14 6-16 16-8 24 24-6 46 10 50 30-14-4-26 0-28 10 14 8 18 26 12 42-10-14-22-16-34-12-4-26-34-46-76-44-40 2-70 22-72 56-4-2-6 12-6 24z" fill="url(#ihair)"/>
        <path d="M722 452c38-14 78-14 116 0" stroke="#5a3d22" stroke-width="8" fill="none"/>
        <circle cx="752" cy="448" r="18" fill="#8a6a3a"/><circle cx="752" cy="448" r="12" fill="#97ff5a" class="lens"/>
        <circle cx="808" cy="448" r="18" fill="#8a6a3a"/><circle cx="808" cy="448" r="12" fill="#2c3440"/>
        <path d="M740 478l26-4M796 474l26 4" stroke="#7e8ea6" stroke-width="7" stroke-linecap="round"/>
        <ellipse cx="755" cy="496" rx="11" ry="12" fill="#fff"/><circle cx="760" cy="498" r="6" fill="#1d2a2a" class="pupil"/>
        <ellipse cx="806" cy="496" rx="11" ry="12" fill="#fff"/><circle cx="811" cy="498" r="6" fill="#1d2a2a" class="pupil"/>
        <path d="M780 500c-4 12-8 20-2 24" stroke="#b38670" stroke-width="4" fill="none"/>
        <path class="smouth" d="M756 540c16 6 32 6 48 0" stroke="#6b3a34" stroke-width="5" fill="none" stroke-linecap="round"/>
      </g>
    </g>
  </g>
  <g class="shot dial">
    <rect width="1600" height="900" fill="#081410"/>
    <circle cx="800" cy="470" r="330" fill="#12211c" stroke="#3a5a50" stroke-width="10"/>
    <circle cx="800" cy="470" r="290" fill="#0b1712" stroke="#97ff5a" stroke-opacity=".25" stroke-width="4"/>
    <path d="M540 470a260 260 0 0 1 520 0" stroke="#97ff5a" stroke-width="26" fill="none" stroke-opacity=".7"/>
    <path d="M540 470a260 260 0 0 1 120-219" stroke="#ff4f6d" stroke-width="26" fill="none"/>
    <text x="560" y="300" fill="#ff4f6d" font-family="Chakra Petch, sans-serif" font-size="40" font-weight="700" text-anchor="middle" transform="rotate(-40 560 300)">REVERSE</text>
    <text x="1040" y="300" fill="#97ff5a" font-family="Chakra Petch, sans-serif" font-size="40" font-weight="700" text-anchor="middle" transform="rotate(40 1040 300)">EVOLVE</text>
    <text x="800" y="620" fill="#6b8a7e" font-family="Chakra Petch, sans-serif" font-size="30" font-weight="600" text-anchor="middle" letter-spacing="8">EVO-RAY MK III</text>
    <g class="needle"><path d="M790 470l10-250 10 250z" fill="#fff"/><circle cx="800" cy="470" r="30" fill="#3b4f48" stroke="#fff" stroke-width="4"/></g>
    <g class="finger"><path d="M1600 760c-200-10-340-40-420-80-30-16-20-50 12-44 60 10 120 20 160 12l248-8z" fill="#e7c8b4"/><path d="M1180 680c-24-14-14-44 14-40" stroke="#c9a28c" stroke-width="4" fill="none"/></g>
  </g>
  <g class="shot zapshot">
    <circle cx="800" cy="450" r="520" fill="url(#ibeam)" class="beam"/>
  </g>
  <g class="shot scope">
    <rect width="1600" height="900" fill="#000"/>
    <circle cx="800" cy="450" r="430" fill="url(#iscope)"/>
    <g class="swim">${micro}</g>
    <g class="hero"><circle cx="800" cy="450" r="70" fill="url(#ihero)" stroke="#c9ff8a" stroke-width="4"/><circle cx="782" cy="430" r="22" fill="#c9ff8a" fill-opacity=".7"/><ellipse cx="826" cy="440" rx="11" ry="13" fill="#fff"/><circle cx="829" cy="442" r="6" fill="#0b1a12"/></g>
    <circle cx="800" cy="450" r="430" fill="none" stroke="#000" stroke-width="40"/>
    <path d="M800 20v40M800 840v40M370 450h40M1190 450h40" stroke="#97ff5a" stroke-opacity=".5" stroke-width="3"/>
  </g>
</svg>`;
}

export class Intro {
  readonly root = el('div', 'screen cine');
  private raf = 0;
  private done?: () => void;
  private skipped = false;

  constructor(private readonly audio: AudioApi) {}

  /** Play the cinematic. Resolves when finished or skipped. */
  play(opts: { subtitles: boolean; reducedMotion: boolean }): Promise<void> {
    this.skipped = false;
    this.root.innerHTML = `${labSvg()}<div class="bars t"></div><div class="bars b"></div><div class="zap"></div><div class="white"></div>
      <div class="caption"></div><div class="subs"><span class="who">The Scientist</span><span class="t"></span></div>
      <div class="skip-hint show">${ICONS.skip}<span>Tap to skip</span></div>`;
    this.root.classList.add('visible');
    const svg = this.root.querySelector('svg') as SVGSVGElement;
    const q = <T extends Element = SVGGElement>(s: string) => svg.querySelector(s) as unknown as T;
    const shots = { lab: q('.shot.lab'), dial: q('.shot.dial'), zap: q('.shot.zapshot'), scope: q('.shot.scope') };
    const cam = q('.lab .cam');
    const needle = q('.dial .needle');
    const needleS = q('.needle-s');
    const finger = q('.dial .finger');
    const beam = q('.zapshot .beam');
    const hero = q('.scope .hero');
    const swim = q('.scope .swim');
    const arm = q('.sci .arm');
    const pupils = svg.querySelectorAll<SVGElement>('.sci .pupil');
    const mouth = q('.sci .smouth');
    const caption = this.root.querySelector('.caption') as HTMLElement;
    const subs = this.root.querySelector('.subs') as HTMLElement;
    const subT = subs.querySelector('.t') as HTMLElement;
    const zapEl = this.root.querySelector('.zap') as HTMLElement;
    const white = this.root.querySelector('.white') as HTMLElement;
    const show = (g: SVGGElement, v: boolean) => g.setAttribute('visibility', v ? 'visible' : 'hidden');

    const lines = INTRO_LINES.map((l) => ({ ...l, started: false, ended: false }));
    const cues = new Set<string>();
    const cue = (name: string, at: number, t: number, fn: () => void) => {
      if (t >= at && !cues.has(name)) {
        cues.add(name);
        fn();
      }
    };
    const start = performance.now();
    this.audio.duck(0.55, DURATION);

    return new Promise<void>((resolve) => {
      this.done = () => {
        cancelAnimationFrame(this.raf);
        this.root.classList.remove('visible');
        this.root.innerHTML = '';
        this.done = undefined;
        resolve();
      };
      const tick = () => {
        this.raf = requestAnimationFrame(tick);
        const rt = (performance.now() - start) / 1000;
        const t = warp(rt);
        // --- shot selection
        show(shots.lab, t < 8.6 || (t >= 11.2 && t < 15.2));
        show(shots.dial, t >= 8.6 && t < 11.2);
        show(shots.zap, t >= 11.2 && t < 12.6);
        show(shots.scope, t >= 15.2);
        // --- lab camera: slow push, then shake + shrink-away
        if (t < 8.6) {
          const k = t / 8.6;
          cam.setAttribute('transform', `translate(${-60 * k} ${-20 * k}) scale(${1 + 0.08 * k})`);
          arm.setAttribute('transform', `rotate(${Math.sin(t * 2) * 3} 860 620)`);
        } else if (t >= 11.2 && t < 15.2) {
          const k = Math.min(1, (t - 12.4) / 2.8);
          const shake = t < 13 && !opts.reducedMotion ? (Math.random() - 0.5) * 24 : 0;
          const s = 1.08 + Math.max(0, k) ** 2 * 14;
          const rot = Math.max(0, k) * 70;
          cam.setAttribute('transform', `translate(${800 + shake} ${450 + shake}) rotate(${rot}) scale(${s}) translate(-${800 + 60} -${450 + 20})`);
          (shots.lab as SVGGElement).style.opacity = String(1 - Math.max(0, k - 0.6) / 0.4);
          pupils.forEach((p) => p.setAttribute('r', '9'));
          mouth.setAttribute('d', 'M756 540c16 18 32 18 48 0');
        }
        // --- dial: needle wobbles EVOLVE → slips to REVERSE
        if (t >= 8.6 && t < 11.2) {
          const k = t - 8.6;
          let ang = 55 + Math.sin(k * 6) * 4;
          if (k > 1.5) ang = 55 - Math.min(1, (k - 1.5) / 0.35) * 118 + Math.sin(k * 40) * (k < 2.1 ? 3 : 0);
          needle.setAttribute('transform', `rotate(${ang} 800 470)`);
          finger.setAttribute('transform', `translate(${Math.max(0, 1.4 - k) * 300 + (k > 1.5 ? Math.min(1, (k - 1.5) / 0.35) * -60 : 0)} ${k > 1.5 ? Math.min(1, (k - 1.5) / 0.35) * 30 : 0})`);
          needleS.setAttribute('transform', `rotate(${ang - 55} 1335 560)`);
        }
        cue('click', 10.1, t, () => this.audio.sfx('ui_click', { volume: 0.9, pitch: 0.6 }));
        cue('warn', 10.4, t, () => this.audio.sfx('warning', { volume: 0.7 }));
        // --- zap
        cue('zap', 11.2, t, () => {
          zapEl.classList.add('go');
          this.audio.sfx('zap', { volume: 1 });
          this.audio.sfx('portal', { volume: 0.8 });
        });
        if (t >= 11.2 && t < 12.6) {
          const k = (t - 11.2) / 1.4;
          beam.setAttribute('transform', `translate(800 450) scale(${0.2 + k * 3}) translate(-800 -450)`);
          (beam as SVGElement).style.opacity = String(1 - k);
        }
        cue('shrink', 12.8, t, () => this.audio.sfx('evolve', { volume: 0.6, pitch: 0.5 }));
        // --- scope
        if (t >= 15.2) {
          const k = Math.min(1, (t - 15.2) / 2.5);
          const e = 1 - (1 - k) ** 3;
          (shots.scope as SVGGElement).style.opacity = String(Math.min(1, (t - 15.2) / 0.8));
          swim.setAttribute('transform', `translate(800 450) scale(${1.6 - e * 0.6}) rotate(${(1 - e) * -40}) translate(-800 -450)`);
          const pulse = 1 + Math.sin(t * 3) * 0.04;
          hero.setAttribute('transform', `translate(800 450) scale(${(0.3 + e * 0.7) * pulse}) translate(-800 -450)`);
        }
        cue('cap1', 0.6, t, () => {
          caption.textContent = 'A lab. Somewhere. Now-ish.';
          caption.classList.add('show');
        });
        cue('cap1off', 5, t, () => caption.classList.remove('show'));
        cue('cap2', 16.2, t, () => {
          caption.textContent = '3.8 billion years ago';
          caption.classList.add('show');
        });
        cue('white', STORY_END - 1.6, t, () => white.classList.add('go'));
        // --- subtitles + voice
        for (const l of lines) {
          if (!l.started && rt >= l.at) {
            l.started = true;
            subT.innerHTML = escapeHtml(l.text).replace(/\*burp\*/gi, '<em>*burp*</em>');
            subs.classList.toggle('show', opts.subtitles);
            this.audio.voice(l.text, { mood: l.mood });
            if (/\*burp\*/i.test(l.text)) setTimeout(() => this.audio.sfx('burp', { volume: 0.7 }), 1300);
          }
          if (l.started && !l.ended && rt >= l.at + l.dur) {
            l.ended = true;
            if (!lines.some((o) => o !== l && o.started && !o.ended)) subs.classList.remove('show');
          }
        }
        if (rt >= DURATION) this.finish();
      };
      this.raf = requestAnimationFrame(tick);
    });
  }

  get playing(): boolean {
    return !!this.done;
  }

  skip(): void {
    if (this.skipped) return;
    this.skipped = true;
    this.finish();
  }

  private finish(): void {
    this.audio.duck(0, 0.1);
    this.done?.();
  }
}
